import apiClient from './apiClient';

// ============================================================================
// INTERFACES
// ============================================================================

export interface EscopoMes {
  id: number;
  mes: number;
  ano: number;
  status: string;
  data_inicio: string;
  data_fim: string;
  created_at: string;
}

export interface VinculoResponse {
  id: number;
  auditor_id: number;
  auditor_nome: string;
  auditor_email: string;
  hospital_id: number;
  hospital_nome: string;
  hospital_cnpj: string;
  cidade: string;
  estado: string;
  regiao: string;
}

export interface AgendamentoItem {
  id: number;
  auditor_id: number | null;
  auditor_nome: string;
  cliente_id: number;
  cliente_nome: string;
  // N180 item 7: três estados. string = código cadastrado; '' = o vínculo
  // cliente×hospital existe e o código está em branco; null = não há vínculo.
  // A tela distingue os dois últimos no tooltip — antes davam a mesma célula
  // vazia e ninguém sabia o que preencher.
  codigo_erp: string | null;
  hospital_id: number;
  hospital_nome: string;
  mes_ano: string;
  hospital_agendado: boolean;
  cliente_hospital_agendado: boolean;
  // T21: o backend devolve o status REAL do job (9 valores + NAO_AGENDADO).
  // N179: faltava NAO_AUTORIZADA, que o backend emite desde o N21c.
  status: 'NAO_AGENDADO' | 'PRE_AGENDADA' | 'AGENDADA' | 'CONFIRMADA' | 'REAGENDADA'
    | 'EM_EXECUCAO' | 'EXECUTADA' | 'NAO_EXECUTADA' | 'NAO_AUTORIZADA' | 'CANCELADA';
  data_agendamento: string | null;
  hora_inicio: string | null;
  hora_fim: string | null;
  pontos_contagem: string;
  job_id: number | null;
}

export interface AgendamentoListResponse {
  contadores: {
    total: number;
    nao_agendado: number;
    pre_agendada: number;
    agendada: number;
    em_execucao: number;
    executada: number;
    // N180: 'nao_autorizada' saiu de dentro de 'cancelada' — o card somava os
    // dois e a Alana fatura por status.
    nao_autorizada: number;
    cancelada: number;
  };
  itens: AgendamentoItem[];
}

export interface ForaEscopoItem {
  contagem_cliente_id: number;
  job_id: number;
  cliente_id: number;
  cliente_nome: string;
  hospital_id: number;
  hospital_nome: string;
  data_prevista: string | null;
  auditor_nome: string;
  status: string;
  motivo: 'SEM_ESCOPO' | 'ADICIONAL';
}

export interface ForaEscopoResponse {
  total: number;
  itens: ForaEscopoItem[];
}

export interface UploadResult {
  success: boolean;
  total_linhas: number;
  total_match: number;
  novos: number;
  substituidos: number;
  errors: string[];
}

export interface ImportResult {
  success: boolean;
  created: number;
  updated: number;
  errors: string[];
  total_processado: number;
  total_erros: number;
}

export interface DashboardStatusItem {
  status: string;
  qtde: number;
  percentual: number;
}

export interface DashboardRegiaoItem {
  regiao: string;
  qtde: number;
  percentual: number;
}

export interface DashboardResponse {
  total_escopo: number;
  pendente_agendamento: number;
  status: DashboardStatusItem[];
  regioes: DashboardRegiaoItem[];
}

export interface DropdownOption {
  id: number;
  nome: string;
  /** N114: só o dropdown de hospitais preenche estes — usados para buscar por
   *  código ou CNPJ, não só pelo nome. Opcionais porque auditores/clientes
   *  usam o mesmo tipo. */
  codigo?: string;
  cnpj?: string;
}

// ============================================================================
// SERVICE
// ============================================================================

const BASE = '/escopo-contagem';

const escopoContagemService = {
  // === Escopo management ===
  async listarEscopos(): Promise<EscopoMes[]> {
    const res = await apiClient.get(`${BASE}/`);
    return res.data;
  },

  async criarEscopo(data: { mes: number; ano: number; data_inicio: string; data_fim: string }): Promise<EscopoMes> {
    const res = await apiClient.post(`${BASE}/`, data);
    return res.data;
  },

  // === Vínculo endpoints (Tab 1) ===
  async listarVinculos(params?: Record<string, string>): Promise<VinculoResponse[]> {
    const res = await apiClient.get(`${BASE}/vinculos/`, { params });
    return res.data;
  },

  async criarVinculo(auditorId: number, hospitalId: number): Promise<any> {
    const res = await apiClient.post(`${BASE}/vinculos/`, {
      auditor_id: auditorId,
      hospital_id: hospitalId,
    });
    return res.data;
  },

  async deletarVinculo(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/vinculos/${id}/`);
  },

  async exportVinculos(): Promise<Blob> {
    const res = await apiClient.get(`${BASE}/vinculos/export/`, { responseType: 'blob' });
    return res.data;
  },

  async importVinculos(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post(`${BASE}/vinculos/import/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  async templateVinculos(): Promise<Blob> {
    const res = await apiClient.get(`${BASE}/vinculos/template/`, { responseType: 'blob' });
    return res.data;
  },

  // === Dashboard ===
  async dashboardEscopo(escopoId: number, clienteId?: number): Promise<DashboardResponse> {
    const params: Record<string, string> = {};
    if (clienteId) params.cliente_id = String(clienteId);
    const res = await apiClient.get(`${BASE}/${escopoId}/dashboard/`, { params });
    return res.data;
  },

  // === Agendamento endpoints (Tab 2) ===
  async listarAgendamento(escopoId: number, params?: Record<string, string>): Promise<AgendamentoListResponse> {
    const res = await apiClient.get(`${BASE}/${escopoId}/agendamento/`, { params });
    return res.data;
  },

  async agendarItem(escopoId: number, itemId: number, payload: { data_prevista: string; hora_inicio: string; hora_fim: string; auditor_id?: number; item_ids?: number[]; auditor_ids?: number[]; justificativas_intervalo?: any[] }): Promise<any> {
    const res = await apiClient.post(`${BASE}/${escopoId}/agendamento/${itemId}/agendar/`, payload);
    return res.data;
  },

  // N115: `auditor_ids` é number[], por isso o valor deixou de ser só string.
  async editarItem(
    escopoId: number,
    itemId: number,
    payload: Record<string, string | number[]>,
  ): Promise<any> {
    const res = await apiClient.patch(`${BASE}/${escopoId}/agendamento/${itemId}/`, payload);
    return res.data;
  },

  async removerItem(escopoId: number, itemId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${escopoId}/agendamento/${itemId}/`);
  },

  // Remove o hospital (EscopoItem) do escopo — exclui o item, nao so cancela o job.
  async excluirItemEscopo(escopoId: number, itemId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${escopoId}/itens/${itemId}/`);
  },

  // Fora do Escopo: agendamentos do mes que nao estao cobertos pelo escopo.
  async listarForaDoEscopo(escopoId: number): Promise<ForaEscopoResponse> {
    const res = await apiClient.get(`${BASE}/${escopoId}/fora-do-escopo/`);
    return res.data;
  },

  // Master inclui um agendamento fora do escopo no escopo (cria EscopoItem).
  async incluirNoEscopo(
    escopoId: number,
    payload: { cliente_id: number; hospital_id: number; job_id?: number | null },
  ): Promise<{ success: boolean; created: boolean; item_id: number }> {
    const res = await apiClient.post(`${BASE}/${escopoId}/fora-do-escopo/incluir/`, payload);
    return res.data;
  },

  async uploadAgendamento(escopoId: number, clienteId: number | null, file: File): Promise<UploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    const url = clienteId
      ? `${BASE}/${escopoId}/upload-agendamento?cliente_id=${clienteId}`
      : `${BASE}/${escopoId}/upload-agendamento`;
    const res = await apiClient.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  async exportAgendamento(escopoId: number): Promise<Blob> {
    const res = await apiClient.get(`${BASE}/${escopoId}/agendamento/export/`, { responseType: 'blob' });
    return res.data;
  },

  async templateAgendamento(escopoId: number): Promise<Blob> {
    const res = await apiClient.get(`${BASE}/${escopoId}/upload-template`, { responseType: 'blob' });
    return res.data;
  },

  // === Dropdown data ===
  async dropdownAuditores(): Promise<DropdownOption[]> {
    const res = await apiClient.get(`${BASE}/dropdown/auditores/`);
    return res.data;
  },

  async dropdownClientes(): Promise<DropdownOption[]> {
    const res = await apiClient.get(`${BASE}/dropdown/clientes/`);
    return res.data;
  },

  async dropdownHospitais(): Promise<DropdownOption[]> {
    const res = await apiClient.get(`${BASE}/dropdown/hospitais/`);
    return res.data;
  },

  // === Dropdown ALL (for add hospital modal) ===
  async dropdownHospitaisTodos(): Promise<DropdownOption[]> {
    const res = await apiClient.get(`${BASE}/dropdown/hospitais/`, { params: { all: 'true' } });
    return res.data;
  },

  async dropdownClientesTodos(): Promise<DropdownOption[]> {
    const res = await apiClient.get(`${BASE}/dropdown/clientes/`, { params: { all: 'true' } });
    return res.data;
  },

  // N132: só os clientes que têm vaga NESTE mês — é o que o modal de limpeza
  // pode oferecer. Sem o escopo_id o endpoint devolve cliente de qualquer mês.
  async dropdownClientesDoEscopo(escopoId: number): Promise<DropdownOption[]> {
    const res = await apiClient.get(`${BASE}/dropdown/clientes/`, { params: { escopo_id: escopoId } });
    return res.data;
  },

  // === Limpar escopo (N82) ===
  // Apaga as vagas do mês SEM cancelar agendamentos: eles vão para "Fora do
  // Escopo" e voltam sozinhos quando a nova carga trouxer o hospital.
  // N132: com clienteId, limpa só as vagas daquele cliente.
  async limparEscopo(escopoId: number, clienteId?: number): Promise<{
    success: boolean;
    removidos: number;
    agendamentos_preservados: number;
    vagas_em_execucao_mantidas: number;
    cliente_id: number | null;
    message: string;
  }> {
    const res = await apiClient.post(`${BASE}/${escopoId}/limpar/`, null, {
      params: clienteId ? { cliente_id: clienteId } : undefined,
    });
    return res.data;
  },

  // === Add hospital manual ===
  async addHospitalManual(
    escopoId: number,
    payload: { hospital_id: number; cliente_id: number; pontos_contagem?: string }
  ): Promise<{ success: boolean; message: string; data?: { id: number; auditor_nome: string | null } }> {
    const res = await apiClient.post(`${BASE}/${escopoId}/agendamento/manual/`, payload);
    return res.data;
  },
};

export default escopoContagemService;
